import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import TopPage from "./pages/TopPage";
import DepotListPage from "./pages/DepotListPage";
import DepotPage from "./pages/DepotPage";
import LoginPage from "./pages/LoginPage";
import Register from "./pages/Register";
import VerifiedPage from "./pages/VerifiedPage";
import HamburgerMenu from "./components/HamburgerMenu";
import ArchivePage from "./pages/ArchivePage";
import RulePage from "./pages/RulePage";
import PrivacyPage from './pages/PrivacyPage';
import BbsPage from "./pages/BbsPage";
import AdminMenuPage from "./pages/AdminMenuPage";
import AdminUserPostsPage from "./pages/AdminUserPostsPage";
import AdminUserManagementPage from "./pages/AdminUserManagementPage";
import AdminReportPage from "./pages/AdminReportPage";
import ContributionPage from "./pages/ContributionPage";
import BusManager from "./pages/BusManager";
import RouteManager from "./pages/RouteManager";
import AdminStartsPage from "./pages/AdminStartsPage";
import DepotManager from "./pages/DepotManager";
import AdminNoticesPage from "./pages/AdminNoticesPage";
import SpecialDateManager from "./pages/SpecialDateManager";
import AnalysisPage from "./pages/AnalysisPage";
import AnalysisBusPage from "./pages/AnalysisBusPage";
import AnalysisEventsPage from "./pages/AnalysisEventsPage";
import AnalysisDutiesPage from "./pages/AnalysisDutiesPage";
import AnalysisDutyPage from "./pages/AnalysisDutyPage";
import AnalysisRoutePage from "./pages/AnalysisRoutePage";
import AnalysisStopsPage from "./pages/AnalysisStopsPage";
import AnalysisLineMapPage from "./pages/AnalysisLineMapPage";
import AnalysisAuditPage from "./pages/AnalysisAuditPage";
import AnalysisHealthPage from "./pages/AnalysisHealthPage";
import AnalysisCoveragePage from "./pages/AnalysisCoveragePage";
import MyPage from "./pages/MyPage";
import FavoritesPage from "./pages/FavoritesPage";
import UserPage from "./pages/UserPage";
import axios from "./axios";
import DayTypeProvider from "./components/DayTypeProvider";
import { canUseAnalysis, canUseAnalysisInternals, canUseLineMap } from "./utils/analysisAccess";

function MainLayout() {
  return (
    <main
      style={{
        maxWidth: "800px",
        margin: "0 auto",
        paddingTop: "16px",
        paddingLeft: "12px",
        paddingRight: "12px",
        position: "relative",
      }}
    >
      <Outlet />
    </main>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get("/user");
        setCurrentUser(res.data);
      } catch (e) {
        localStorage.removeItem("token");
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  function AdminRoute({ element }) {
    if (currentUser?.user_role_id !== 1) return <Navigate to="/" replace />;
    return element;
  }

  function AnalysisRoute({ element }) {
    // 見せない段階では存在ごと伏せる（管理画面と同じ扱い）
    if (!canUseAnalysis(currentUser)) return <Navigate to="/" replace />;
    return element;
  }

  // 解析そのものを見る3画面。**運用解析より狭い。**
  // 乗番の中身ではなく、乗番が正しいかどうかを見る側なので、
  // 公開範囲を広げてもここは付いてこない
  function AnalysisInternalsRoute({ element }) {
    if (!canUseAnalysisInternals(currentUser)) return <Navigate to="/" replace />;
    return element;
  }

  function LineMapRoute({ element }) {
    if (!canUseLineMap(currentUser)) return <Navigate to="/" replace />;
    return element;
  }

  if (loading) return <div style={{ padding: "16px" }}>読み込み中...</div>;

  return (
    <DayTypeProvider>
    <Router>
      <HamburgerMenu
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
      />

      <Routes>
        {/* 認証ページ：main ラッパーなし（全画面レイアウト） */}
        <Route path="/login" element={<LoginPage setCurrentUser={setCurrentUser} />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verified" element={<VerifiedPage />} />

        {/* その他全ページ：MainLayout（padded main）経由 */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<TopPage currentUser={currentUser} />} />
          <Route path="/rule" element={<RulePage />} />
          <Route path="/depots" element={<DepotListPage />} />
          <Route path="/depots/:depot" element={<DepotPage currentUser={currentUser} />} />
          <Route path="/bbs" element={<BbsPage currentUser={currentUser} />} />
          <Route path="/archive" element={<ArchivePage currentUser={currentUser} />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contribution" element={<ContributionPage />} />
          <Route path="/users/:userId" element={<UserPage />} />
          <Route
            path="/mypage"
            element={
              currentUser
                ? <MyPage currentUser={currentUser} setCurrentUser={setCurrentUser} />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/favorites"
            element={
              currentUser
                ? <FavoritesPage currentUser={currentUser} />
                : <Navigate to="/login" replace />
            }
          />
          {/* 運用解析（260826追加）。公開範囲は utils/analysisAccess.js で切り替える。
              バックエンド側（routes/api.php の運用解析 group）も一緒に変えること */}
          <Route path="/analysis"                 element={<AnalysisRoute element={<AnalysisPage currentUser={currentUser} />} />} />
          <Route path="/analysis/events"          element={<AnalysisRoute element={<AnalysisEventsPage />} />} />
          <Route path="/analysis/buses/:busNumber" element={<AnalysisRoute element={<AnalysisBusPage />} />} />
          <Route path="/analysis/duties"          element={<AnalysisRoute element={<AnalysisDutiesPage />} />} />
          <Route path="/analysis/duties/:dutyId"  element={<AnalysisRoute element={<AnalysisDutyPage />} />} />
          <Route path="/analysis/routes/:routeId" element={<AnalysisRoute element={<AnalysisRoutePage />} />} />
          {/* バス停から探す（260905追加）。車号が付いた便だけを並べる */}
          <Route path="/analysis/stops"           element={<AnalysisRoute element={<AnalysisStopsPage />} />} />
          {/* 解析そのものを見る 3 画面（260903追加）。
              乗番の中身ではなく、乗番が正しいかどうかを見る側。
              **ここだけ super_admin のまま**（ANALYSIS_INTERNALS_ACCESS） */}
          <Route path="/analysis/audit"           element={<AnalysisInternalsRoute element={<AnalysisAuditPage />} />} />
          <Route path="/analysis/health"          element={<AnalysisInternalsRoute element={<AnalysisHealthPage />} />} />
          <Route path="/analysis/coverage"        element={<AnalysisInternalsRoute element={<AnalysisCoveragePage />} />} />
          {/* 路線図（260915追加）。2026-09-23 にログインユーザーへ公開。
              公開範囲は LINE_MAP_ACCESS（解析の様子の 3 画面とは別）。
              変えるときは backend/routes/api.php の line-maps も一緒に */}
          <Route path="/analysis/line-map"        element={<LineMapRoute element={<AnalysisLineMapPage />} />} />
          <Route path="/analysis/line-map/:key"   element={<LineMapRoute element={<AnalysisLineMapPage />} />} />

          <Route path="/admin"                  element={<AdminRoute element={<AdminMenuPage currentUser={currentUser} />} />} />
          <Route path="/admin/users-posts"      element={<AdminRoute element={<AdminUserPostsPage currentUser={currentUser} />} />} />
          <Route path="/admin/users-management" element={<AdminRoute element={<AdminUserManagementPage currentUser={currentUser} />} />} />
          <Route path="/admin/reports"          element={<AdminRoute element={<AdminReportPage currentUser={currentUser} />} />} />
          <Route path="/admin/depots"           element={<AdminRoute element={<DepotManager />} />} />
          <Route path="/admin/buses"            element={<AdminRoute element={<BusManager />} />} />
          <Route path="/admin/routes"           element={<AdminRoute element={<RouteManager />} />} />
          <Route path="/admin/starts"           element={<AdminRoute element={<AdminStartsPage currentUser={currentUser} />} />} />
          <Route path="/admin/notices"          element={<AdminRoute element={<AdminNoticesPage />} />} />
          <Route path="/admin/special-dates"    element={<AdminRoute element={<SpecialDateManager />} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Router>
    </DayTypeProvider>
  );
}

export default App;
